import { alpha } from '@mui/material/styles';
import { ICustomTheme } from '../types/ICustomTheme';
import { MODERN_BORDER, MODERN_SHADOWS, MODERN_TRANSITIONS } from './modernVariables';

export const getSharedInputStyles = (themeOptions: ICustomTheme) => ({
     '& .MuiInputBase-root': {
          borderRadius: MODERN_BORDER.radius.md,
          transition: MODERN_TRANSITIONS.smooth,
          position: 'relative' as const,

          // Glassmorphism sutil
          backgroundColor: themeOptions.palette.mode === 'dark' ? alpha(themeOptions.palette.background.paper, 0.6) : alpha(themeOptions.palette.background.paper, 0.8),
          backdropFilter: 'blur(8px)',

          '&.Mui-focused': {
               backgroundColor: themeOptions.palette.background.paper,
               boxShadow: MODERN_SHADOWS.glow.primary(themeOptions.palette.primary.main),
               transform: 'translateY(-1px)',
          },

          '&:hover:not(.Mui-focused)': {
               backgroundColor: themeOptions.palette.mode === 'dark' ? alpha(themeOptions.palette.background.paper, 0.8) : alpha(themeOptions.palette.background.paper, 0.95),
               transform: 'translateY(-0.5px)',
               boxShadow: MODERN_SHADOWS.elevation[1],
          },
     },

     '& .MuiInputBase-input': {
          color: themeOptions.palette.text.primary,
          fontWeight: 400,

          '&::placeholder': {
               opacity: 0.7,
               transition: MODERN_TRANSITIONS.fast,
          },

          '&:focus::placeholder': {
               opacity: 0.5,
          },
     },

     '& .MuiInputLabel-root': {
          color: themeOptions.palette.text.secondary,
          fontWeight: 500,
          transition: MODERN_TRANSITIONS.fast,

          '&.Mui-focused': {
               color: themeOptions.palette.primary.main,
               fontWeight: 600,
          },

          '&.Mui-error': {
               color: themeOptions.palette.error.main,
          },
     },

     '& .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(themeOptions.palette.text.secondary, 0.3),
          borderWidth: '1px',
          transition: MODERN_TRANSITIONS.border,
     },

     '& .MuiOutlinedInput-root': {
          '&:hover .MuiOutlinedInput-notchedOutline': {
               borderColor: alpha(themeOptions.palette.text.primary, 0.5),
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
               borderColor: themeOptions.palette.primary.main,
               borderWidth: '2px',
          },

          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
               borderColor: themeOptions.palette.error.main,
               borderWidth: '2px',
               animation: 'errorBorder 0.3s ease-in-out',
          },
     },

     '& .MuiFormHelperText-root': {
          color: themeOptions.palette.text.secondary,
          fontWeight: 400,
          fontSize: '0.75rem',
          marginTop: '6px',

          '&.Mui-error': {
               color: themeOptions.palette.error.main,
               fontWeight: 500,
          },
     },

     // Animação de erro
     '@keyframes errorBorder': {
          '0%, 100%': {
               borderColor: themeOptions.palette.error.main,
          },
          '50%': {
               borderColor: alpha(themeOptions.palette.error.main, 0.7),
               boxShadow: `0 0 8px ${alpha(themeOptions.palette.error.main, 0.3)}`,
          },
     },
});

export const getSharedDataGridStyles = (themeOptions: ICustomTheme) => ({
     width: '100%',
     borderRadius: MODERN_BORDER.radius.xl,
     borderColor: alpha(themeOptions.palette.divider, 0.3),
     backgroundColor: themeOptions.palette.background.paper,
     boxShadow: MODERN_SHADOWS.elevation[2],
     transition: MODERN_TRANSITIONS.smooth,
     overflow: 'hidden',

     // Glassmorphism para DataGrid
     backdropFilter: 'blur(8px)',
     border: `1px solid ${alpha(themeOptions.palette.divider, 0.2)}`,

     '&:hover': {
          boxShadow: MODERN_SHADOWS.elevation[3],
          borderColor: alpha(themeOptions.palette.primary.main, 0.2),
     },

     '& .MuiDataGrid-row': {
          borderBottom: `1px solid ${alpha(themeOptions.palette.divider, 0.3)}`,
          transition: MODERN_TRANSITIONS.fast,

          '&:hover': {
               backgroundColor: themeOptions.palette.mode === 'dark' ? alpha(themeOptions.palette.primary.main, 0.08) : alpha(themeOptions.palette.primary.main, 0.04),
               transform: 'translateY(-1px)',
               boxShadow: `inset 0 1px 0 ${alpha(themeOptions.palette.primary.main, 0.1)}`,
          },

          '&.Mui-selected': {
               backgroundColor: alpha(themeOptions.palette.primary.main, 0.12),
               '&:hover': {
                    backgroundColor: alpha(themeOptions.palette.primary.main, 0.16),
               },
          },
     },

     '& .MuiDataGrid-columnHeaders': {
          backgroundColor: alpha(themeOptions.palette.background.default, 0.5),
          borderBottom: `2px solid ${alpha(themeOptions.palette.divider, 0.2)}`,
          backdropFilter: 'blur(4px)',
     },

     '& .MuiDataGrid-columnHeader': {
          fontWeight: 600,
          fontSize: '0.875rem',
          color: themeOptions.palette.text.primary,

          '&:hover': {
               backgroundColor: alpha(themeOptions.palette.primary.main, 0.04),
          },
     },

     '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
     },

     '& .MuiDataGrid-columnSeparator': {
          color: alpha(themeOptions.palette.divider, 0.5),
     },

     '& .MuiDataGrid-cell': {
          color: themeOptions.palette.text.primary,
          borderBottom: `1px solid ${alpha(themeOptions.palette.divider, 0.2)}`,
          borderRight: `1px solid ${alpha(themeOptions.palette.divider, 0.1)}`,
          display: 'flex',
          alignItems: 'center',

          '&:focus': {
               outline: `2px solid ${alpha(themeOptions.palette.primary.main, 0.5)}`,
               outlineOffset: '-2px',
          },
     },
     '& .MuiDataGrid-footerContainer': {
          backgroundColor: alpha(themeOptions.palette.background.default, 0.5),
          borderTop: `1px solid ${alpha(themeOptions.palette.divider, 0.2)}`,
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          minHeight: '56px',

          // Melhor distribuição dos elementos de paginação
          '& .MuiTablePagination-root': {
               width: '100%',
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               overflow: 'visible',

               // Centralizar os controles de paginação
               '& .MuiTablePagination-toolbar': {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0',
                    minHeight: '40px',
                    flexWrap: 'wrap',
                    gap: '8px',
               },

               // Estilizar o texto de contagem de linhas
               '& .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: themeOptions.palette.text.secondary,
                    margin: '0',
                    order: 1,
                    flexShrink: 0,
               },

               // Estilizar o seletor de linhas por página
               '& .MuiTablePagination-selectLabel': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: themeOptions.palette.text.secondary,
                    margin: '0 8px 0 0',
               },

               // Container do seletor e controles centrais
               '& .MuiInputBase-root': {
                    marginLeft: '8px',
                    marginRight: '16px',

                    '& .MuiSelect-select': {
                         fontSize: '0.875rem',
                         fontWeight: 500,
                         padding: '4px 24px 4px 8px',
                         minWidth: '60px',
                    },
               },

               // Centralizar os botões de navegação
               '& .MuiTablePagination-actions': {
                    marginLeft: 'auto',
                    order: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',

                    '& .MuiIconButton-root': {
                         width: '36px',
                         height: '36px',
                         borderRadius: MODERN_BORDER.radius.md,
                         transition: MODERN_TRANSITIONS.fast,
                         border: `1px solid ${alpha(themeOptions.palette.divider, 0.3)}`,
                         backgroundColor: alpha(themeOptions.palette.background.paper, 0.5),

                         '&:hover': {
                              backgroundColor: alpha(themeOptions.palette.primary.main, 0.08),
                              borderColor: alpha(themeOptions.palette.primary.main, 0.3),
                              transform: 'translateY(-1px)',
                              boxShadow: MODERN_SHADOWS.elevation[1],
                         },
                         '&:disabled': {
                              backgroundColor: alpha(themeOptions.palette.text.secondary, 0.12),
                              borderColor: alpha(themeOptions.palette.divider, 0.2),
                              opacity: 0.5,
                         },

                         '& .MuiSvgIcon-root': {
                              fontSize: '1.2rem',
                              color: themeOptions.palette.text.primary,
                         },
                    },
               },
          },

          // Responsividade para telas menores
          '@media (max-width: 768px)': {
               flexDirection: 'column',
               padding: '12px 16px',
               gap: '8px',

               '& .MuiTablePagination-root .MuiTablePagination-toolbar': {
                    flexDirection: 'column',
                    gap: '12px',
                    alignItems: 'center',
               },

               '& .MuiTablePagination-displayedRows': {
                    order: 1,
                    textAlign: 'center',
               },

               '& .MuiTablePagination-selectLabel, & .MuiInputBase-root': {
                    order: 2,
               },

               '& .MuiTablePagination-actions': {
                    order: 3,
                    marginLeft: '0',
               },
          },

          '@media (max-width: 480px)': {
               '& .MuiTablePagination-root .MuiTablePagination-toolbar': {
                    '& .MuiTablePagination-selectLabel': {
                         fontSize: '0.8rem',
                    },
                    '& .MuiTablePagination-displayedRows': {
                         fontSize: '0.8rem',
                    },
               },

               '& .MuiTablePagination-actions .MuiIconButton-root': {
                    width: '32px',
                    height: '32px',

                    '& .MuiSvgIcon-root': {
                         fontSize: '1rem',
                    },
               },
          },
     },
     '& .MuiDataGrid-toolbarContainer': {
          backgroundColor: alpha(themeOptions.palette.background.default, 0.3),
          borderBottom: `1px solid ${alpha(themeOptions.palette.divider, 0.2)}`,
          backdropFilter: 'blur(4px)',
          padding: '12px 16px',
     },

     // Estilos específicos para densidade compacta (padrão)
     '&.MuiDataGrid-root--densityCompact': {
          '& .MuiDataGrid-cell': {
               padding: '4px 8px',
               fontSize: '0.875rem',
               lineHeight: 1.4,
          },
          '& .MuiDataGrid-columnHeader': {
               padding: '4px 8px',
               fontSize: '0.875rem',
               minHeight: '40px',
          },
          '& .MuiDataGrid-row': {
               minHeight: '36px !important',
               maxHeight: '36px !important',
          },
          '& .MuiDataGrid-cell--textLeft, & .MuiDataGrid-cell--textRight, & .MuiDataGrid-cell--textCenter': {
               alignItems: 'center',
          },
     },
});
